import { X } from 'lucide-react';

interface FlowHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

interface Step {
  id: string;
  label: string;
  detail: string;
  parallel?: boolean;
}

interface Phase {
  title: string;
  subtitle: string;
  steps: Step[];
}

const PHASES: Phase[] = [
  {
    title: 'Phase 1 — Intake',
    subtitle: 'POST /issue-analysis/intake (invoked inside /analyze-stream)',
    steps: [
      {
        id: '1a',
        label: 'Fetch JIRA ticket',
        detail: 'Summary, description, comments, linked issues, attachments.',
      },
      {
        id: '1b',
        label: 'Run OCR on image attachments',
        detail: 'Claude vision reads up to 3 images in parallel; text is appended to the analysis context.',
      },
      {
        id: '1c',
        label: 'LLM extract + condensed summary',
        detail: 'After OCR completes, entity / initial-module / issue-type extraction runs in parallel with a ~150-word LLM summary. Both see the OCR-enriched text.',
        parallel: true,
      },
      {
        id: '1d',
        label: 'Canonicalise modules against Neo4j catalogue',
        detail: 'Rewrite LLM-returned module names to the exact catalogue form (e.g. "Customer Master" → "CustomerMaster"). Then run a keyword-sweep safety net over the ticket text for modules the LLM missed.',
      },
      {
        id: '1e',
        label: 'Fetch Support Docs + Knowledge Bank',
        detail: 'Support Docs (chat pipeline, Support persona) and Knowledge Bank (embedding search) run in parallel using the condensed summary + initial modules.',
        parallel: true,
      },
      {
        id: '1f',
        label: 'Build enriched context',
        detail: 'Combine ticket text + OCR + Support Docs answer + doc titles + similar-issue summaries/modules into one enriched text blob.',
      },
      {
        id: '1g',
        label: 'Re-detect modules on enriched context',
        detail: 'LLM module-only extraction runs in parallel with the keyword sweep. New matches are canonicalised and unioned into the module list; the result is deduped.',
        parallel: true,
      },
    ],
  },
  {
    title: 'Phase 2 — Data Retrieval',
    subtitle: 'Sub-phases run in parallel where possible',
    steps: [
      {
        id: '2.1',
        label: 'Resolve tables + query ERAX',
        detail: 'For each confirmed module, Neo4j returns implicated tables / suspect fields. ERAX generates SQL per entity and runs it against the client database via LRP.',
      },
      {
        id: '2.2',
        label: 'Generate timelines',
        detail: 'LRP Event Tracker queries per high/medium-confidence entity produce chronological state transitions.',
      },
      {
        id: '2.3',
        label: 'Support Docs (reuse from Phase 1)',
        detail: 'No re-fetch — the docs answer and citations collected in step 1e are passed through.',
      },
      {
        id: '2.4',
        label: 'Knowledge Bank (reuse from Phase 1)',
        detail: 'No re-fetch — similar past issues from step 1e are passed through.',
      },
      {
        id: '2.5',
        label: 'Gap detection',
        detail: 'For each data source, emit an explicit DataGap entry when nothing was found.',
      },
    ],
  },
  {
    title: 'Phase 3 — Structured Analysis',
    subtitle: 'Single LLM reasoning call grounded in all evidence collected',
    steps: [
      {
        id: '3',
        label: 'Generate final analysis',
        detail: 'Issue definition, differential diagnosis (ranked hypotheses), root cause analysis, and resolution plan (immediate workaround + permanent fix).',
      },
    ],
  },
];

export function FlowHelpDialog({ open, onClose }: FlowHelpDialogProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">How Issue Analysis works</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              The pipeline that runs when you click Analyze — same steps the progress bar announces.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Phases */}
        <div className="px-5 py-4 space-y-5">
          {PHASES.map((phase) => (
            <section key={phase.title}>
              <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">
                {phase.title}
              </h4>
              <p className="text-[11px] text-gray-500 mb-2">{phase.subtitle}</p>
              <ol className="space-y-2">
                {phase.steps.map((step) => (
                  <li
                    key={step.id}
                    className="flex gap-3 p-2 border border-gray-100 rounded bg-gray-50/60"
                  >
                    <span className="text-[10px] font-mono font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded h-fit flex-shrink-0">
                      {step.id}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-800">{step.label}</span>
                        {step.parallel && (
                          <span className="text-[9px] text-green-700 bg-green-50 border border-green-200 rounded px-1 py-0.5 uppercase tracking-wide">
                            parallel
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-600 mt-0.5 leading-relaxed">
                        {step.detail}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}

          {/* Footer note */}
          <section className="pt-2 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-800">Why Support Docs is tab 2</h4>
            <p className="text-[11px] text-gray-600 leading-relaxed mt-1">
              Tab 1 (JIRA Ticket) and Tab 2 (Support Docs) are the two sources whose text feeds
              module detection. Keeping them adjacent makes that dependency visible — if a module
              like "Haulage Contract" only appears in the Support Docs answer, the re-detection
              step in 1g picks it up and adds it to the Detected Modules chips.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
